import { Button, Space, Switch, Table } from "antd";
import { useTranslation } from "react-i18next";

import type { ColumnsType } from "antd/es/table";
import { IClinic } from "@interfaces";
import { EditOutlined } from "@ant-design/icons";
import { useNotify } from "@utils";

import { memo } from "react";
import { Link } from "react-router-dom";
import { PATH_ADMINS, PATH_CLINIC } from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";
import { clinicApi } from "@api";

interface IProps {
  data: IClinic[];
  callback: () => void;
}
export const ClinicTable = memo(({ data, callback }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { success, error } = useNotify();

  const dataSource: IClinic[] = data;

  const columns: ColumnsType<IClinic> = [
    {
      title: t("page.provider_name"),
      dataIndex: "clinicName",
      key: "1",
      sorter: (a, b) => {
        return a.clinicName.localeCompare(b.clinicName);
      },
    },
    {
      title: t("page.phone_number"),
      dataIndex: "phoneNumber",
      key: "2",
    },
    {
      title: t("page.email"),
      dataIndex: "email",
      key: "3",
      sorter: (a, b) => {
        return a.email.localeCompare(b.email);
      },
    },
    {
      title: t("page.joined_program"),
      dataIndex: "joinedProgram",
      key: "4",
      render: (_, data) => (
        <Space>
          {data?.joinedProgram.join(" ,")}
        </Space>
      )
    },
    {
      title: t("page.actions"),
      key: "5",
      render: (_, record) => (
        <Space size="middle">
          <Switch checked={record?.status} onChange={(value: boolean) => onChange(value, record)} />
          <Space>
            <Link
              style={
                record?.status ? { pointerEvents: "auto" } : { pointerEvents: "none", opacity: 0.7 }
              }
              to={PATH_CLINIC + `/${record.clinicId}`}
            >
              <Button type="ghost" icon={<EditOutlined />} size={"small"} />
              <span>{t("page.edit")}</span>
            </Link>
          </Space>
        </Space>
      ),
    },
  ];

  const onChange = async (value: boolean, record: IClinic) => {
    try {
      dispatch(setLoading(true));
      await clinicApi.update({ ...record, status: value }, record.clinicId);
      callback();
      success(t("message.update.success"));
    } catch (err: any) {
      error(t("message.update.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  return <Table dataSource={dataSource} rowKey={"userId"} columns={columns} pagination={false} />;
});
